import { useEffect, useRef } from "react";
import * as THREE from "three";
import { ControlPanel } from "./ControlPanel";

export interface CRTFrameProps {
  children?: React.ReactNode;
  scene?: THREE.Group | THREE.Group[];
  /** Scale of the entire TV model (default: 6) */
  scale?: number;
  /** Position of the TV in the scene */
  position?: [number, number, number];
  /** Rotation of the TV in the scene */
  rotation?: [number, number, number];
  /** Screen horizontal offset adjustment */
  screenOffsetX?: number;
  /** Screen vertical offset adjustment */
  screenOffsetY?: number;
  /** Screen depth offset adjustment */
  screenOffsetZ?: number;
  /** Scale of the screen content */
  screenScale?: number;
  /** Screen tilt in radians - negative tilts back, positive tilts forward */
  screenTilt?: number;
  /** Enable shadow casting (default: true) */
  castShadow?: boolean;
  /** Enable shadow receiving (default: true) */
  receiveShadow?: boolean;
  /** Whether the TV is powered on (for button indicator) */
  isPoweredOn?: boolean;
  /** Called when power button is pressed */
  onPowerPress?: () => void;
  /** Called when volume up is pressed */
  onVolumeUpPress?: () => void;
  /** Called when volume down is pressed */
  onVolumeDownPress?: () => void;
  /** Called when channel up is pressed */
  onChannelUpPress?: () => void;
  /** Called when channel down is pressed */
  onChannelDownPress?: () => void;
  /** Called when action button is pressed */
  onActionPress?: () => void;
  /** Called when video button is pressed */
  onVideoPress?: () => void;
  /** Show control panel (default: true) */
  showControlPanel?: boolean;
}

/**
 * CRT.Frame - 3D CRT TV model that wraps screen content
 *
 * Must be placed inside a CRT.Root. Renders the 3D TV model and positions
 * children (typically CRT.Screen) on the screen surface.
 *
 * @example
 * ```tsx
 * <CRT.Root>
 *   <CRT.Frame position={[0, 0, 0]} scale={6}>
 *     <CRT.Screen>
 *       {content}
 *     </CRT.Screen>
 *   </CRT.Frame>
 * </CRT.Root>
 * ```
 */
export function CRTFrame({
  children,
  scene,
  scale = 6,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  screenOffsetX = 0,
  screenOffsetY = 0.005,
  screenOffsetZ = 0.14,
  screenScale = 1,
  screenTilt = 0.003, // Slight backward tilt to match monitor angle
  castShadow = true,
  receiveShadow = true,
  isPoweredOn = true,
  onPowerPress,
  onVolumeUpPress,
  onVolumeDownPress,
  onChannelUpPress,
  onChannelDownPress,
  onActionPress,
  onVideoPress,
  showControlPanel = true,
}: CRTFrameProps) {
  const groupRef = useRef<THREE.Group>(null);
  const modelRef = useRef<THREE.Group>(null);

  // Enable shadows on all meshes in the GLTF scene
  useEffect(() => {
    if (!scene) return;

    const sceneArray = Array.isArray(scene) ? scene : [scene];
    for (const s of sceneArray) {
      s.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = castShadow;
          child.receiveShadow = receiveShadow;
        }
      });
    }
  }, [scene, castShadow, receiveShadow]);

  if (!scene) {
    return null;
  }

  return (
    <group ref={groupRef} position={position} rotation={rotation}>
      <group ref={modelRef} scale={scale}>
        <primitive
          object={scene}
          position={[0, -0.23, -0.01]}
          rotation={[0, Math.PI * 1, 0]}
        />
      </group>

      {/* Control panel - positioned on the TV frame */}
      {showControlPanel && (
        <ControlPanel
          scale={scale}
          position={[-0.685, -1.071, -0.035]}
          rotation={[0, -0.02, -0.002]}
          isPoweredOn={isPoweredOn}
          onPowerPress={onPowerPress}
          onVolumeUpPress={onVolumeUpPress}
          onVolumeDownPress={onVolumeDownPress}
          onChannelUpPress={onChannelUpPress}
          onChannelDownPress={onChannelDownPress}
          onActionPress={onActionPress}
          onVideoPress={onVideoPress}
        />
      )}

      {/* Screen content - positioned on the monitor's screen surface */}
      <group
        position={[
          screenOffsetX + 0.001,
          screenOffsetY + 0.2,
          screenOffsetZ - 0.2,
        ]}
        rotation={[screenTilt, 0, 0]}
        scale={
          new THREE.Vector3(
            screenScale * 1.21,
            screenScale * 1,
            screenScale * 1,
          )
        }
      >
        {children}
      </group>
    </group>
  );
}
